import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { api, formatApiError } from "../../lib/api";

const EMPTY = { name: "", organisation: "", phone: "", email: "", purpose: "" };
const FIELDS = [
  ["name", "Your name", "text", true],
  ["organisation", "Organisation / department", "text", true],
  ["phone", "Phone", "tel", true],
  ["email", "Email", "email", true],
];

export const certificateWhatsApp = (title) =>
  `https://wa.me/919742036439?text=${encodeURIComponent(`Hello Vata Infrastructures, I would like a copy of the work done certificate for: ${title}`)}`;

export const CertificateRequestDialog = ({ work, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (work && user) setForm((f) => ({ ...f, name: f.name || user.name, organisation: f.organisation || user.organisation, phone: f.phone || user.phone, email: f.email || user.email }));
  }, [work, user]);

  const close = () => { setForm(EMPTY); setDone(false); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/certificate-requests", { ...form, work_id: work.id, work_title: work.title });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!work} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="cert-request-dialog" className="max-w-lg rounded-none border-0 bg-[#f4f0e8] p-0">
        <div className="bg-[#161a19] p-6 text-white">
          <p className="font-mono text-xs uppercase tracking-wider text-[#e0764f]">Request work done certificate</p>
          <DialogTitle className="mt-2 font-display text-2xl font-bold uppercase leading-tight">{work?.title}</DialogTitle>
          <DialogDescription className="mt-1 text-sm text-white/60">{work?.dept}</DialogDescription>
        </div>
        {done ? (
          <div data-testid="cert-request-success" className="p-8 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-[#1c2a22]" />
            <p className="mt-4 font-display text-2xl font-bold uppercase">Request received</p>
            <p className="mt-2 text-sm text-[#5b605c]">Darshan will send a copy of the certificate to {form.email} shortly.</p>
            {user && user.role !== "admin" && <Link to="/account" onClick={close} data-testid="cert-request-track-link" className="mt-3 block text-sm font-semibold text-[var(--rust)] underline">Track it in My account</Link>}
            <button type="button" onClick={close} data-testid="cert-request-close" className="btn-rust mt-6 px-6 py-3">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 p-6" data-testid="cert-request-form">
            <div className="grid gap-4 sm:grid-cols-2">
              {FIELDS.map(([k, label, type]) => (
                <div key={k} className="space-y-1.5">
                  <Label htmlFor={`cr-${k}`} className="text-xs uppercase tracking-wider">{label}</Label>
                  <Input id={`cr-${k}`} type={type} required value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`cert-request-${k}-input`} className="rounded-none border-black/20 bg-white" />
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cr-purpose" className="text-xs uppercase tracking-wider">Purpose (optional)</Label>
              <Textarea id="cr-purpose" rows={2} maxLength={500} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                data-testid="cert-request-purpose-input" className="rounded-none border-black/20 bg-white" placeholder="e.g. Tender evaluation, bid capacity check" />
            </div>
            {!user && (
              <p data-testid="cert-request-signin-hint" className="text-xs text-[#5b605c]">
                <Link to="/login?next=/account" className="font-semibold text-[#b44a2c] underline">Sign in</Link> or <Link to="/register" className="font-semibold text-[#b44a2c] underline">create an account</Link> to track your requests.
              </p>
            )}
            <div className="flex flex-col gap-3 pt-2 sm:flex-row">
              <button type="submit" disabled={busy} data-testid="cert-request-submit" className="btn-rust inline-flex flex-1 items-center justify-center gap-2 py-3 disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send request
              </button>
              <a href={work ? certificateWhatsApp(work.title) : "#"} target="_blank" rel="noopener noreferrer" data-testid="cert-request-whatsapp"
                className="inline-flex flex-1 items-center justify-center gap-2 bg-[#25D366] py-3 text-sm font-semibold uppercase tracking-wider text-white transition-opacity hover:opacity-90">
                <WhatsAppIcon className="h-4 w-4" /> Ask on WhatsApp
              </a>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
